import { useSelector } from 'react-redux';
import { format, differenceInDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useTheme, Box, Typography, Chip, Divider } from "@mui/material";
import { getDateFromDateTime, convertDecimalToTime } from "state/helpers";
import { hasPermission } from 'state/checkPermission';
import FlexBetween from "components/FlexBetween";


const addDaySuffix = (time, dayDiff) => {
    if (!time) return 'N/A';
    if (dayDiff === 1) return `${time}+`;
    if (dayDiff > 1) return `${time}+${dayDiff}`;
    return time;
};

const getTime = (time, routeDate) => {
    if (!time) return 'N/A';
    const date = getDateFromDateTime(time);
    if (!date || isNaN(new Date(date))) return 'N/A';
    try {
        return addDaySuffix(formatInTimeZone(time, 'UTC', 'HH:mm'), differenceInDays(date, routeDate));
    } catch {
        return 'N/A';
    }
};

const getRouteDate = (route, timeMode) => {
    const fullDT = timeMode === 'UTC'
        ? (route.fullAtd || route.fullEtd)
        : (route.fullAtdLocalBase || route.fullEtdLocalBase);
    return getDateFromDateTime(fullDT);
};


const RouteList = ({ routes, onView }) => {
    const timeMode = useSelector((state) => state.timemode);
    const user     = useSelector((state) => state.user);
    const params   = useSelector((state) => state.params);
    const theme    = useTheme();

    const canView = hasPermission(user?.permissions, "CrewRoutes", "view", false);
    const minCrewRouteTAT = Number(params?.find((p) => p.param === "MINIMUM_CREW_ROUTE_TAT_MINUTES")?.value) || 80;

    if (!routes?.length)
        return <Typography sx={{ mt: 2 }}>No routes available.</Typography>;

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {routes.map((route, idx) => {
                const routeDate = getRouteDate(route, timeMode);

                const fullDT = timeMode === 'UTC'
                    ? (route.fullAtd || route.fullEtd)
                    : (route.fullAtdLocalBase || route.fullEtdLocalBase);
                const fullAT = timeMode === 'UTC'
                    ? (route.fullAta || route.fullEta)
                    : (route.fullAtaLocalBase || route.fullEtaLocalBase);

                const sortedCrew = [...(route.crewMembers || [])].sort((a, b) => a.rankPriority - b.rankPriority);

                const hasWarning = route.routeDetails?.length
                    ? route.routeDetails.some((f) => f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT)
                    : route.flights?.some((f) => f.fdp && f.maxFDP && f.fdp > f.maxFDP) || false;

                const overFdp = route.flights?.reduce((sum, f) => (
                    f.fdp != null && f.maxFDP != null && f.fdp > f.maxFDP
                        ? sum + (f.fdp - f.maxFDP)
                        : sum
                ), 0) ?? 0;

                return (
                    <Box
                        key={route._id || idx}
                        sx={{
                            p: 1.2,
                            backgroundColor: "background.paper",
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "neutral.deep",
                            "&:active": { transform: "scale(0.98)", boxShadow: 1 },
                        }}
                        onClick={() => { canView && onView(route); }}
                    >
                        <Box
                            sx={{
                                borderRadius: 1,
                                animation: hasWarning ? "breathingGlow 3s ease-in-out infinite" : "none",
                                "@keyframes breathingGlow": {
                                    "0%, 100%": { transform: "scale(1)",    boxShadow: "0 0 0px rgba(255, 215, 0, 0.25)" },
                                    "50%":       { transform: "scale(1.01)", boxShadow: "0 0 1px 1.5px rgba(190, 190, 80, 0.6)" },
                                },
                            }}
                        >
                            <FlexBetween>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Chip
                                        label={idx + 1}
                                        variant="outlined"
                                        color={hasWarning ? "warning" : "primary"}
                                        size="small"
                                        sx={{ fontSize: "0.75rem", borderWidth: 1.5, height: 20, "& .MuiChip-label": { px: 0.8 } }}
                                    />
                                    <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                                        {routeDate ? format(routeDate, "dd/MM/yy") : "N/A"}
                                    </Typography>
                                    <Typography
                                        variant="subtitle2"
                                        title={route.crewRoute}
                                        sx={{ lineHeight: 1, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                    >
                                        {route.crewRoute?.length > 23 ? route.crewRoute.slice(0, 23) + "..." : route.crewRoute || ""}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                    {route.acType || ""} | {route.flightReg?.split("-")[1] || ""}
                                </Typography>
                            </FlexBetween>

                            <FlexBetween>
                                <Typography variant="subtitle2">{getTime(fullDT, routeDate)}</Typography>
                                <Typography
                                    variant="subtitle2"
                                    title={route.flightNo}
                                    sx={{ lineHeight: 1, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                >
                                    {route.flightNo?.length > 35 ? route.flightNo.slice(0, 35) + "..." : route.flightNo || ""}
                                </Typography>
                                <Typography variant="subtitle2">{getTime(fullAT, routeDate)}</Typography>
                            </FlexBetween>

                            <Typography variant="subtitle2" color="error.main" textAlign="center">
                                {overFdp > 0 && `FDP exceeded: ${convertDecimalToTime(overFdp)}`}
                            </Typography>
                        </Box>

                        <Divider sx={{ my: 0.5 }} />

                        {sortedCrew.map((member, i) => (
                            <Box
                                key={i}
                                display="flex" alignItems="center" gap={1}
                                sx={{ color: member?.gender === 'FM' ? theme.palette.secondary.main : theme.palette.primary.dark }}
                            >
                                <Typography width="18px" textAlign="right">{i + 1}.</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: 30 }}>
                                    {member.crewCode}
                                </Typography>
                                <Typography variant="body2">{member.crewName}</Typography>
                                <Typography variant="body2">
                                    ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                );
            })}
        </Box>
    );
};

export default RouteList;
